/**
 * Google Tag Manager Component
 * Injects GTM scripts and provides tracking utilities
 * Respects cookie consent preferences
 */
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getCookieConsent, getCookiePreferences } from './CookieConsent';
import config from 'config';

const API_URL = config.API_URL;

// Initialize dataLayer globally
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Push event to GTM dataLayer
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }
};

/**
 * Track page view
 */
export const trackPageView = (pagePath, pageTitle) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

/**
 * Track form submission
 */
export const trackFormSubmission = (formName, formData = {}) => {
  trackEvent('form_submission', {
    form_name: formName,
    ...formData
  });
};

/**
 * Track contact request
 */
export const trackContactRequest = (serviceType, isBusiness = false) => {
  trackEvent('contact_request', {
    service_type: serviceType,
    is_business: isBusiness
  });
};

/**
 * Track booking/reservation
 */
export const trackBooking = (bookingData = {}) => {
  trackEvent('booking', {
    booking_type: bookingData.type || 'standard',
    vehicle_type: bookingData.vehicle,
    destination: bookingData.destination,
    value: bookingData.price
  });
};

/**
 * Track CTA click
 */
export const trackCTAClick = (ctaName, ctaLocation) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation
  });
};

// Global flag to prevent multiple GTM initializations
let gtmInitialized = false;

/**
 * Inject GTM scripts into the page
 */
const injectGTM = (gtmId) => {
  if (gtmInitialized || !gtmId) return;
  
  // Check if GTM script already exists
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) {
    gtmInitialized = true;
    return;
  }

  // Initialize dataLayer with gtm.start event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Create and inject main GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  
  // Insert as first script in head for earliest possible loading
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  // Create and inject noscript iframe
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  
  // Insert noscript right after body opening tag
  if (document.body.firstChild) {
    document.body.insertBefore(noscript, document.body.firstChild);
  } else {
    document.body.appendChild(noscript);
  }

  gtmInitialized = true;
  console.log('GTM initialized with ID:', gtmId);
};

/**
 * GoogleTagManager Component
 * Fetches GTM ID from backend and injects scripts
 * Also tracks page views on route changes
 */
export default function GoogleTagManager() {
  const [gtmId, setGtmId] = useState(null);
  const location = useLocation();

  // Fetch GTM configuration from backend
  useEffect(() => {
    const fetchGTMConfig = async () => {
      // Check cookie consent first
      const consent = getCookieConsent();
      const prefs = getCookiePreferences();
      
      // Only load GTM if analytics cookies are accepted
      if (!consent?.accepted || !prefs?.analytics) {
        console.log('GTM not loaded: analytics cookies not accepted');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/settings/public/tracking`);
        if (response.ok) {
          const data = await response.json();
          if (data.enabled && data.gtm_container_id) {
            setGtmId(data.gtm_container_id);
            // Inject immediately when config is received
            injectGTM(data.gtm_container_id);
          }
        }
      } catch (error) {
        console.warn('GTM config fetch failed:', error);
      }
    };

    fetchGTMConfig();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (gtmId && gtmInitialized) {
      // Small delay to ensure page title is updated
      setTimeout(() => {
        trackPageView(location.pathname, document.title);
      }, 100);
    }
  }, [location.pathname, gtmId]);

  // This component doesn't render anything visible
  return null;
}
